import { Component, computed, OnInit, signal, ViewChild } from '@angular/core';
import { ControlContainer, FormsModule, NgForm, NgModelGroup } from '@angular/forms';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { YEAR_FORMATS } from 'app/constants/filter.constants';
import { IVehiclesGroup } from 'app/models/filter-form.models';
import { IAutocompleteOption } from 'app/models/filter.models';
import { FilterService } from 'app/services/filter.service';
import { makeControlDirty, mapValuesToOptions } from 'app/utils/form.utils';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import moment from 'moment';
import { FilterItemComponent } from '../filter-item/filter-item.component';
import { MultiSelectWithSearchComponent } from '../../form/multiselect-with-search/multiselect-with-search.component';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { StateCountryService } from '../../../../services/state-country.service';
import { MatAutocomplete, MatAutocompleteTrigger, MatOptgroup, MatOption } from '@angular/material/autocomplete';

@Component({
  selector: 'app-vehicles-tab',
  templateUrl: './vehicles-tab.component.html',
  styleUrls: ['./vehicles-tab.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
  providers: [provideMomentDateAdapter(YEAR_FORMATS)],
  imports: [
    FormsModule,
    FilterItemComponent,
    MultiSelectWithSearchComponent,
    MatFormField,
    MatLabel,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatAutocomplete,
    MatOption,
    MatAutocompleteTrigger,
    MatOptgroup,
  ],
})
export class VehiclesTabComponent implements OnInit {
  private readonly countries: Record<string, string> = {
    CAN: 'Canada',
    USA: 'United States',
    MEX: 'Mexico',
  };
  @ViewChild('vehicles') ngModelGroup!: NgModelGroup;

  colorOptions: IAutocompleteOption[] = [];
  makeOptions: IAutocompleteOption[] = [];
  modelOptions: IAutocompleteOption[] = [];
  typeOptions: IAutocompleteOption[] = [];
  readonly stateFilter = signal('');
  readonly states = computed(() => {
    const groups: Record<string, IAutocompleteOption[]> = {};
    for (const { id, stateName, countryCode } of this.statesService.states()) {
      if (!groups[countryCode]) {
        groups[countryCode] = [];
      }
      if (
        this.stateFilter() &&
        !stateName.toLowerCase().includes(this.stateFilter().toLowerCase()) &&
        !id.includes(this.stateFilter())
      ) {
        continue;
      }
      groups[countryCode].push({ id: stateName, value: id });
    }
    return Object.entries(groups).map(([group, values]) => ({
      group: this.countries[group] ?? group,
      values,
    }));
  });

  vehiclesGroup: IVehiclesGroup = {
    colors: undefined,
    makes: undefined,
    models: undefined,
    types: undefined,
    releaseDateFrom: undefined,
    releaseDateTo: undefined,
    licensePlate: undefined,
    state: undefined,
  };

  today = new Date();
  releaseMinDate = new Date(1900, 0);

  constructor(
    private filterService: FilterService,
    private readonly statesService: StateCountryService,
  ) {}

  ngOnInit(): void {
    this.filterService.getVehicles().subscribe((filters) => {
      this.colorOptions = mapValuesToOptions(filters.colors);
      this.makeOptions = mapValuesToOptions(filters.makes);
      this.modelOptions = mapValuesToOptions(filters.models);
      this.typeOptions = mapValuesToOptions(filters.types);
    });

    this.initializeFilters();
  }

  get vehicleColors() {
    return this.vehiclesGroup.colors?.map((x) => x.value).join(', ') ?? '';
  }

  get vehicleMakes() {
    return this.vehiclesGroup.makes?.map((x) => x.value).join(', ') ?? '';
  }

  get vehicleModels() {
    return this.vehiclesGroup.models?.map((x) => x.value).join(', ') ?? '';
  }

  get vehicleTypes() {
    return this.vehiclesGroup.types?.map((x) => x.value).join(', ') ?? '';
  }

  get releaseYears(): string {
    const { releaseDateFrom, releaseDateTo } = this.vehiclesGroup;
    let dateFrom, dateTo;
    if (releaseDateFrom) {
      dateFrom = moment(releaseDateFrom).format('YYYY');
    }

    if (releaseDateTo) {
      dateTo = moment(releaseDateTo).format('YYYY');
    }

    if (releaseDateFrom && releaseDateTo) {
      return `${dateFrom} - ${dateTo}`;
    }

    if (releaseDateFrom) return `${dateFrom}`;

    if (releaseDateTo) return `${dateTo}`;

    return '';
  }

  get licensePlate(): string {
    return [this.vehiclesGroup.licensePlate, this.displayState(this.vehiclesGroup.state ?? '')]
      .filter(Boolean)
      .join(',');
  }

  displayState(val: string) {
    return this.statesService.findById(val)?.stateName ?? '';
  }

  clearFilter(
    filter: 'vehicleColors' | 'vehicleMakes' | 'vehicleModels' | 'vehicleTypes' | 'releaseDate' | 'licensePlate',
  ): () => void {
    return () => {
      makeControlDirty(this.ngModelGroup.control);

      switch (filter) {
        case 'vehicleColors':
          this.vehiclesGroup.colors = undefined;
          break;
        case 'vehicleMakes':
          this.vehiclesGroup.makes = undefined;
          break;
        case 'vehicleModels':
          this.vehiclesGroup.models = undefined;
          break;
        case 'vehicleTypes':
          this.vehiclesGroup.types = undefined;
          break;
        case 'releaseDate':
          this.vehiclesGroup.releaseDateFrom = undefined;
          this.vehiclesGroup.releaseDateTo = undefined;
          break;
        case 'licensePlate':
          this.vehiclesGroup.licensePlate = undefined;
          this.vehiclesGroup.state = undefined;
          break;
      }
    };
  }

  private initializeFilters() {
    const { appliedFilters: filters } = this.filterService;

    const releaseDateFrom = filters.vehicleFilter?.releaseDateFrom;
    const releaseDateTo = filters.vehicleFilter?.releaseDateTo;
    if (releaseDateFrom) {
      this.vehiclesGroup.releaseDateFrom = releaseDateFrom;
    }
    if (releaseDateTo) {
      this.vehiclesGroup.releaseDateTo = releaseDateTo;
    }
    this.vehiclesGroup.colors = mapValuesToOptions(filters.vehicleFilter?.colors);
    this.vehiclesGroup.makes = mapValuesToOptions(filters.vehicleFilter?.makes);
    this.vehiclesGroup.models = mapValuesToOptions(filters.vehicleFilter?.models);
    this.vehiclesGroup.types = mapValuesToOptions(filters.vehicleFilter?.types);
    this.vehiclesGroup.licensePlate = filters.vehicleFilter?.licensePlate;
    this.vehiclesGroup.state = filters.vehicleFilter?.state;
    this.stateFilter.set(filters.vehicleFilter?.state ?? '');
  }

  onYearSelected(date: Date, datepicker: MatDatepicker<Date>, field: 'releaseDateFrom' | 'releaseDateTo') {
    if (field === 'releaseDateFrom') {
      this.vehiclesGroup.releaseDateFrom = date;
    } else {
      this.vehiclesGroup.releaseDateTo = date;
    }
    makeControlDirty(this.ngModelGroup.control);
    datepicker.close();
  }
}
